import { type ClassValue, clsx } from "clsx"
import { TFunction } from "i18next"
import { extendTailwindMerge } from "tailwind-merge"

const customTwMerge = extendTailwindMerge({
  extend: {
    classGroups: {
      "font-size": [
        "text-headline",
        "text-subheadline",
        "text-title",
        "text-subtitle",
        "text-heading",
        "text-subheading",
        "text-body1",
        "text-body2",
        "text-caption",
        "text-metadata",
      ],
    },
  },
})

export function cn(...inputs: ClassValue[]) {
  return customTwMerge(clsx(inputs))
}

export function validateEmail(email: string) {
  return email.match(
    /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/,
  )
}

export const websiteRegex = /[-a-zA-Z0-9@:%._+~#=]{1,256}\.[a-zA-Z0-9()]{1,6}\b([-a-zA-Z0-9()@:%_+.~#?&//=]*)/gi

export const postCodeRegex = /\b[a-zA-Z0-9]{5,7}\b/g

export interface IIndexable<T> {
  [key: string]: T
}

export const buildMakeKey = (name: string | undefined) => (key: string) => (name ? name + "." + key : key)

export const formatRelativeDays = (dateStr: string, t: TFunction) => {
  const date = new Date(dateStr)
  if (isNaN(date.getTime())) return "unknown"
  const now = new Date()
  const diff = Math.floor((now.getTime() - date.getTime()) / (1000 * 60 * 60 * 24))
  if (diff < 0) return t("dates.inDays", { days: Math.abs(diff) })
  if (diff === 0) return t("dates.today")
  return t("dates.daysAgo", { days: diff })
}

/**
 * Formats a number with dots as thousand separators (European format)
 * @param value - The number to format
 * @returns Formatted string with dots as thousand separators
 */
export function formatNumberWithDots(value: number): string {
  return value.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ".")
}

/**
 * Formats a number to a specified number of decimal places,
 * using the user's locale for correct separators.
 * @param value The number to format.
 * @param decimals The number of decimal places to show.
 * @returns A formatted string.
 */
export const formatNumber = (value: number | null | undefined, decimals: number = 2): string => {
  if (value === null || value === undefined) {
    return "-" // Or "N/A", or whatever you prefer for null values
  }

  // Use the modern Intl.NumberFormat for locale-aware formatting
  return new Intl.NumberFormat(undefined, {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  }).format(value)
}
